import React, { useState } from 'react';

import { useRecoilValue } from 'recoil';
import { PopoverPanel as HeadlessPopoverPanel } from '@headlessui/react';

import { ia } from 'lib/helpers/utility';

import Input from 'components/shared/Input/Input';
import Typography from 'components/shared/Typography/Typography';
import { permissions as permissionsState } from 'components/state';

import PopoverMenu from '../Menu/PopoverMenu';
import PopoverOptions from '../Options/PopoverOptions';
import { checkOptionPermission } from '../../lib/checkOptionPermission';

import { getPanelClasses } from './lib/getClasses';

const SEARCHABLE_OPTIONS_THRESHOLD = 12;

const isSeparatorOption = (option) => option?.type === 'separator' || option?.separator;

// Labels are often JSX; `searchValue` is how such an option stays searchable.
const getOptionSearchText = (option) => {
  const text = option?.searchValue ?? option?.label ?? option?.title;
  return typeof text === 'string' ? text.toLowerCase() : null;
};

const PopoverPanel = ({
  isFixed,
  handlePanelRef,
  panelRef,
  onScroll,
  panelClassName,
  position,
  panelFixedPosition,
  isPositioned,
  options,
  children,
  close,
  // Options props
  optionsGroupBy,
  optionGroupLabelClassName,
  hoveredIndexes,
  handleMouseEnter,
  handleMouseLeave,
  handleSubmenuClose,
  handleOptionClick,
  buttonRef,
  activeOptionRef,
  submenuRefs,
  text,
  customButton,
  optionIconSize,
  optionIconColor,
  iconsShade,
  optionArrowIcon,
  optionArrowIconStroke,
  optionActiveIcon,
  optionArrowColor,
  optionIconNoColor,
  onPanelMouseLeave,
  separatorClassName,
  searchable = true
}) => {
  const permissions = useRecoilValue(permissionsState);
  const [searchTerm, setSearchTerm] = useState('');

  const term = searchTerm.trim().toLowerCase();
  const showSearch =
    searchable &&
    ia(options) &&
    options.filter((option) => !isSeparatorOption(option)).length > SEARCHABLE_OPTIONS_THRESHOLD;
  const topLevelOptions =
    showSearch && term
      ? options.filter((option) => {
          if (isSeparatorOption(option)) return true;
          const text = getOptionSearchText(option);
          if (text === null) return true;
          return text.includes(term);
        })
      : options;

  const renderOptions = (options, level = 1, handleClose, parentPath = '') => {
    const checkOptionPermissionFn = (opt) => checkOptionPermission(permissions)(opt);
    const visibleOptions = options.filter(checkOptionPermissionFn);

    let lastKeptWasSeparator = true;
    const filteredOptions = visibleOptions.filter((opt, index) => {
      const isSeparator = opt.type === 'separator' || opt.separator;

      if (!isSeparator) {
        lastKeptWasSeparator = false;
        return true;
      }

      const itemsAfter = visibleOptions.slice(index + 1);
      const hasNextNonSeparator = itemsAfter.some((o) => o.type !== 'separator' && !o.separator);

      if (!hasNextNonSeparator) return false;
      if (lastKeptWasSeparator) return false;

      lastKeptWasSeparator = true;
      return true;
    });

    if (!ia(filteredOptions)) {
      return (
        <Typography className="px-4 py-2" size="small" color="neutral-500">
          No options available
        </Typography>
      );
    }

    return (
      <PopoverOptions
        options={filteredOptions}
        level={level}
        handleClose={handleClose}
        parentPath={parentPath}
        optionsGroupBy={optionsGroupBy}
        optionGroupLabelClassName={optionGroupLabelClassName}
        hoveredIndexes={hoveredIndexes}
        handleMouseEnter={handleMouseEnter}
        handleMouseLeave={handleMouseLeave}
        handleSubmenuClose={handleSubmenuClose}
        handleOptionClick={handleOptionClick}
        activeOptionRef={activeOptionRef}
        submenuRefs={submenuRefs}
        text={text}
        customButton={customButton}
        optionIconSize={optionIconSize}
        optionIconColor={optionIconColor}
        iconsShade={iconsShade}
        optionArrowIcon={optionArrowIcon}
        optionArrowIconStroke={optionArrowIconStroke}
        optionActiveIcon={optionActiveIcon}
        optionArrowColor={optionArrowColor}
        position={position}
        renderOptions={renderOptions}
        optionIconNoColor={optionIconNoColor}
        separatorClassName={separatorClassName}
      />
    );
  };

  const renderChildren = (close) => {
    if (ia(options))
      return (
        <PopoverMenu isReady={!isFixed || isPositioned} triggerRef={buttonRef}>
          {renderOptions(topLevelOptions, 1, close)}
        </PopoverMenu>
      );
    if (typeof children === 'function') {
      return (
        <div className="flex flex-col overflow-hidden">{children({ closePopover: close })}</div>
      );
    }
    if (children) {
      return <div className="flex flex-col overflow-hidden">{children}</div>;
    }
    return null;
  };

  return (
    <HeadlessPopoverPanel
      ref={isFixed ? handlePanelRef : panelRef}
      portal={isFixed}
      onScroll={onScroll}
      onMouseLeave={onPanelMouseLeave}
      className={getPanelClasses({ isFixed, position, options, panelClassName })}
      style={{
        ...(isFixed ? panelFixedPosition : {}),
        ...(isFixed && !isPositioned ? { visibility: 'hidden' } : {})
      }}>
      {showSearch && (
        <div className="sticky -top-2 z-10 -mt-2 rounded-t-md bg-white">
          <Input
            placeholder="Search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            rightIcon={searchTerm && 'close-linear'}
            rightIconClick={() => setSearchTerm('')}
            inputWrapperClassName="w-full rounded-none border-0 border-b border-neutral-200 px-4"
          />
        </div>
      )}
      {renderChildren(close)}
    </HeadlessPopoverPanel>
  );
};

export default PopoverPanel;
